import {
  type AvailabilitySchedule,
  type DateOverride,
  DEFAULT_WEEKLY_HOURS,
  HOLIDAY_COUNTRIES,
  type HolidayCountry,
  type Locale,
  pickLocale,
  type TimeInterval,
  WEEKDAYS,
  type Weekday,
  type WeeklyHours,
} from '@bb/shared';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import {
  useCreateSchedule,
  useDeleteSchedule,
  useHolidays,
  useSchedules,
  useSetDefaultSchedule,
  useUpdateSchedule,
} from '@/features/booking/api';
import { ApiRequestError } from '@/lib/api';
import { btnPrimary, btnSecondary, inputCls, inputInlineCls } from '@/lib/ui';

export const Route = createFileRoute('/app/booking/availability')({
  component: AvailabilityPage,
});

const TIMEZONES = Intl.supportedValuesOf('timeZone');

function AvailabilityPage() {
  const { t } = useTranslation(['booking', 'common']);
  const { data: schedules = [], isLoading } = useSchedules();
  const { data: user } = useCurrentUser();
  const create = useCreateSchedule();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedId && schedules.length > 0) setSelectedId(schedules[0]?.id ?? null);
  }, [schedules, selectedId]);

  const selected = schedules.find((s) => s.id === selectedId) ?? null;

  const addSchedule = () =>
    create.mutate(
      {
        name: `${t('booking:availability.defaultName')} ${schedules.length + 1}`,
        timezone: user?.timezone ?? 'Asia/Tokyo',
        weekly: DEFAULT_WEEKLY_HOURS,
        overrides: [],
        blockHolidays: user?.timezone === 'Asia/Tokyo' ? ['JP'] : [],
      },
      { onSuccess: (s) => setSelectedId(s.id) },
    );

  if (isLoading) return <p className="text-sm text-neutral-500">{t('common:status.loading')}</p>;

  return (
    <div className="grid gap-6 md:grid-cols-[220px_1fr]">
      <aside className="space-y-2">
        <ul className="space-y-1">
          {schedules.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => setSelectedId(s.id)}
                className={`min-h-11 w-full rounded px-3 py-2 text-left text-sm sm:min-h-0 ${
                  s.id === selectedId ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'
                }`}
              >
                {s.name}
                {s.isDefault ? (
                  <span className="ml-2 text-xs opacity-70">
                    {t('booking:availability.default')}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={addSchedule}
          disabled={create.isPending}
          className={btnSecondary}
        >
          {t('booking:availability.newSchedule')}
        </button>
      </aside>
      {selected ? (
        <ScheduleEditor
          key={selected.id}
          schedule={selected}
          locale={(user?.locale ?? 'en') as Locale}
        />
      ) : null}
    </div>
  );
}

/**
 * A date override plus an identity that does not change when the date does.
 *
 * The rows used to be keyed on the date itself, which meant editing one
 * replaced the whole row: React saw a new key, unmounted the old `<input>` and
 * mounted a fresh one. The browser's date picker belongs to the element it was
 * opened from, so it closed the moment the value moved — which, in Chrome, is
 * as soon as you click an arrow to change month. The calendar shut before you
 * could reach the date you were navigating towards.
 */
interface OverrideRow extends DateOverride {
  rowId: string;
}

let rowCounter = 0;
const withRowId = (o: DateOverride): OverrideRow => ({ ...o, rowId: `ov-${++rowCounter}` });

function ScheduleEditor({ schedule, locale }: { schedule: AvailabilitySchedule; locale: Locale }) {
  const { t } = useTranslation(['booking', 'common', 'errors']);
  const update = useUpdateSchedule();
  const setDefault = useSetDefaultSchedule();
  const remove = useDeleteSchedule();
  const [name, setName] = useState(schedule.name);
  const [timezone, setTimezone] = useState(schedule.timezone);
  const [weekly, setWeekly] = useState<WeeklyHours>(schedule.weekly);
  const [overrides, setOverrides] = useState<OverrideRow[]>(() =>
    schedule.overrides.map(withRowId),
  );
  /** What the server sees: the rows without the identity we added for React. */
  const plainOverrides = useMemo(() => overrides.map(({ rowId: _rowId, ...o }) => o), [overrides]);
  const [blockHolidays, setBlockHolidays] = useState<HolidayCountry[]>(schedule.blockHolidays);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    name !== schedule.name ||
    timezone !== schedule.timezone ||
    JSON.stringify(weekly) !== JSON.stringify(schedule.weekly) ||
    JSON.stringify(plainOverrides) !== JSON.stringify(schedule.overrides) ||
    JSON.stringify(blockHolidays) !== JSON.stringify(schedule.blockHolidays);

  const save = () => {
    setError(null);
    update.mutate(
      { id: schedule.id, name, timezone, weekly, overrides: plainOverrides, blockHolidays },
      { onError: (err) => setError(err instanceof ApiRequestError ? err.code : 'generic') },
    );
  };

  const setDay = (day: Weekday, intervals: TimeInterval[]) =>
    setWeekly({ ...weekly, [day]: intervals });

  const toggleCountry = (c: HolidayCountry) =>
    setBlockHolidays(
      blockHolidays.includes(c) ? blockHolidays.filter((x) => x !== c) : [...blockHolidays, c],
    );

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">{t('booking:availability.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">
            {t('booking:availability.timezone')}
          </span>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className={inputCls}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </label>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-medium">{t('booking:availability.weeklyHours')}</h2>
        <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white">
          {WEEKDAYS.map((day) => (
            <DayRow
              key={day}
              label={weekdayLabel(day, timezone, locale)}
              intervals={weekly[day]}
              onChange={(next) => setDay(day, next)}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium">{t('booking:availability.holidays')}</h2>
        <p className="mb-2 text-xs text-neutral-500">{t('booking:availability.holidaysHelp')}</p>
        {HOLIDAY_COUNTRIES.map((c) => (
          <label key={c} className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0">
            <input
              type="checkbox"
              checked={blockHolidays.includes(c)}
              onChange={() => toggleCountry(c)}
            />
            {t(`booking:availability.holidayCountries.${c}`)}
          </label>
        ))}
        {blockHolidays.includes('JP') ? (
          <UpcomingHolidays country="JP" locale={locale} overrides={plainOverrides} />
        ) : null}
      </section>

      <section>
        <h2 className="text-sm font-medium">{t('booking:availability.overrides')}</h2>
        <p className="mb-2 text-xs text-neutral-500">{t('booking:availability.overridesHelp')}</p>
        <div className="space-y-2">
          {overrides.map((ov, i) => (
            <div key={ov.rowId} className="rounded-lg border border-neutral-200 bg-white">
              <div className="flex items-center gap-3 border-b border-neutral-100 px-3 py-2">
                <input
                  type="date"
                  value={ov.date}
                  onChange={(e) =>
                    setOverrides(
                      overrides.map((o, j) => (j === i ? { ...o, date: e.target.value } : o)),
                    )
                  }
                  className={inputInlineCls}
                />
                <button
                  type="button"
                  onClick={() => setOverrides(overrides.filter((_, j) => j !== i))}
                  className="ml-auto flex min-h-11 items-center text-xs text-red-700 sm:min-h-0"
                >
                  {t('common:actions.delete')}
                </button>
              </div>
              <DayRow
                label=""
                intervals={ov.intervals}
                onChange={(next) =>
                  setOverrides(overrides.map((o, j) => (j === i ? { ...o, intervals: next } : o)))
                }
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setOverrides([
                ...overrides,
                withRowId({ date: new Date().toISOString().slice(0, 10), intervals: [] }),
              ])
            }
            className={btnSecondary}
          >
            {t('booking:availability.addOverride')}
          </button>
        </div>
      </section>

      {error ? <p className="text-sm text-red-600">{t(`errors:${error}`)}</p> : null}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          className={btnPrimary}
        >
          {t('common:actions.save')}
        </button>
        {update.isSuccess && !dirty ? (
          <span className="text-sm text-neutral-500">{t('common:status.saved')}</span>
        ) : null}
        {!schedule.isDefault ? (
          <>
            <button
              type="button"
              onClick={() => setDefault.mutate(schedule.id)}
              className={btnSecondary}
            >
              {t('booking:availability.makeDefault')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('booking:availability.deleteConfirm'))) {
                  remove.mutate(schedule.id, {
                    onError: (err) =>
                      setError(err instanceof ApiRequestError ? err.code : 'generic'),
                  });
                }
              }}
              className={`${btnSecondary} text-red-700`}
            >
              {t('common:actions.delete')}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function UpcomingHolidays({
  country,
  locale,
  overrides,
}: {
  country: HolidayCountry;
  locale: Locale;
  overrides: DateOverride[];
}) {
  const { t } = useTranslation('booking');
  const today = DateTime.now().toISODate() ?? '';
  const until = DateTime.now().plus({ days: 120 }).toISODate() ?? '';
  const { data: holidays = [] } = useHolidays(country, today, until);
  if (holidays.length === 0) return null;
  const opened = new Set(overrides.filter((o) => o.intervals.length > 0).map((o) => o.date));
  return (
    <ul className="mt-2 grid gap-x-6 gap-y-0.5 text-xs text-neutral-600 sm:grid-cols-2">
      {holidays.map((h) => (
        <li key={h.date} className={opened.has(h.date) ? 'line-through opacity-60' : ''}>
          {/*
            Not toFormat('D (ccc)'): in English that is 10/12/2026, which a
            British or Australian reader — much of this product's audience —
            reads as 10 December. A month in words cannot be misread, and the
            preset puts the parts in each locale's own order.
          */}
          {DateTime.fromISO(h.date)
            .setLocale(locale)
            .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}{' '}
          · {pickLocale(h.name, locale)}
          {opened.has(h.date) ? ` — ${t('availability.holidayOpened')}` : ''}
        </li>
      ))}
    </ul>
  );
}

function DayRow({
  label,
  intervals,
  onChange,
}: {
  label: string;
  intervals: TimeInterval[];
  onChange: (next: TimeInterval[]) => void;
}) {
  const { t } = useTranslation('booking');
  const setAt = (i: number, patch: Partial<TimeInterval>) =>
    onChange(intervals.map((iv, j) => (j === i ? { ...iv, ...patch } : iv)));
  const last = intervals[intervals.length - 1];
  const nextStart = last ? last.end : '09:00';
  return (
    <div className="flex flex-wrap items-start gap-2 px-3 py-2 sm:gap-3">
      {label ? <div className="w-10 pt-3 text-sm font-medium sm:pt-1">{label}</div> : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {intervals.length === 0 ? (
          <span className="pt-1 text-sm text-neutral-400">{t('availability.unavailable')}</span>
        ) : null}
        {intervals.map((iv, i) => (
          <div
            /*
              The position, and nothing from the value.

              The key used to include `iv.start`, so every keystroke in the
              hours changed it and React threw the input away and built a new
              one. Typing 1 then 2 gave 01: the second digit landed in a box
              that had just been born, with the first already committed.
            */
            // biome-ignore lint/suspicious/noArrayIndexKey: intervals carry no id; position is their identity
            key={i}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
          >
            <input
              type="time"
              value={iv.start}
              step={300}
              onChange={(e) => setAt(i, { start: e.target.value })}
              className={`${inputInlineCls} min-w-0 flex-1 px-2 sm:flex-none`}
            />
            <span>–</span>
            <input
              type="time"
              value={iv.end}
              step={300}
              onChange={(e) => setAt(i, { end: e.target.value })}
              className={`${inputInlineCls} min-w-0 flex-1 px-2 sm:flex-none`}
            />
            <button
              type="button"
              onClick={() => onChange(intervals.filter((_, j) => j !== i))}
              className="flex size-11 shrink-0 items-center justify-center text-neutral-400 hover:text-red-700 sm:size-6"
              aria-label="remove"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() =>
          onChange([
            ...intervals,
            { start: nextStart, end: nextStart < '17:00' ? '17:00' : '23:00' },
          ])
        }
        className="flex min-h-11 basis-full items-center pl-12 text-left text-xs font-medium underline sm:min-h-0 sm:basis-auto sm:pt-1 sm:pl-0"
      >
        {t('availability.addInterval')}
      </button>
    </div>
  );
}

function weekdayLabel(day: Weekday, timeZone: string, locale: Locale): string {
  /*
    The app's language, not the browser's.

    This passed `undefined`, which means whatever the browser is set to — so an
    account reading Japanese on an English laptop had its week labelled Mon to
    Sun, in the middle of a Japanese page. The holiday list below it was right,
    because it was told the locale.
  */
  // Any week works; 2024-01-01 is a Monday.
  const base = new Date(Date.UTC(2024, 0, 1 + WEEKDAYS.indexOf(day), 12));
  return new Intl.DateTimeFormat(locale === 'ja' ? 'ja-JP' : 'en-GB', {
    weekday: 'short',
    timeZone,
  }).format(base);
}
